import CloseIcon from "@mui/icons-material/Close";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Pagination,
    Slide,
    TextField,
    useMediaQuery,
    useTheme
} from "@mui/material";
import Box from "@mui/material/Box";
import {styled} from "@mui/material/styles";
import {TransitionProps} from "@mui/material/transitions";
import {AxiosRequestConfig} from "axios";
import Loader from "components/loader";
import React, {useCallback, useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {OrcamentosService} from "services/OrcamentosService";
import {ConsumidorOrcamento} from "types/orcamento";
import {RestPage} from "types/prtoheuspage";
import {PAGE_SIZE_SELECT} from "utils/constants";
import {grey} from "@mui/material/colors";
import OrcamentoModalConsumidorRow from "components/orcamento/modals/consumidor/row";
import {useAppSelector} from "store";
import {useConfigState} from "store/config";

type Props = {
    show: boolean;
    setShow: (show: boolean) => void;
    handleSelect: (data: ConsumidorOrcamento) => void;
};

type SearchProps = {
    codigoCliente: string;
    nome: string;
    cpfOuCnpj: string;
};

const SendButton = styled(Button)({
    width: "100%",
    height: "100%",
});

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ModalConsumidor = ({show, setShow, handleSelect}: Props) => {
    const {handleSubmit,register,reset,formState: {errors},} = useForm<SearchProps>();
    const [page, setPage] = useState<RestPage<ConsumidorOrcamento>>();
    const config = useAppSelector(useConfigState);
    const [isLoading, setIsLoading] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [isFirst, setIsFirst] = useState(true);
    const [nome, setNome] = useState("");
    const [cgc, setCgc] = useState("");
    const [codigoCliente, setCodigoCliente] = useState("");
    const xs = useMediaQuery(theme.breakpoints.down("md"));


    const getConsumidores = useCallback(() => {
        const params: AxiosRequestConfig = {
            params: {
                page: pageCount,
                size: pageSize,
                nome: nome,
                cgc: cgc,
                codigoCliente: codigoCliente,
            },
        };
        setIsLoading(true);
        OrcamentosService.findConsumidores(config.empresa.empresaCodigo, params)
            .then((res) => {
                setPage(res.data);
            })
            .catch((err) => {
                toast.error(
                    err.response.data.message || "Erro ao carregar os consumidores"
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    },[cgc, codigoCliente, config.empresa.empresaCodigo, nome, pageCount, pageSize]);


    useEffect(() => {
        if(!isFirst) {
            getConsumidores();
        }
    }, [getConsumidores, isFirst]);


    const handleAction = (originalRow: ConsumidorOrcamento) => {
        handleSelect(originalRow);
        setPageCount(0);
        reset();
    };

    const onSubmit = (data: SearchProps) => {
        setIsFirst(false);
        let cpfOuCnpjTemp = data.cpfOuCnpj.replace(/\D/g, "");
        if (cpfOuCnpjTemp.length === 11 ||cpfOuCnpjTemp.length === 14 ||cpfOuCnpjTemp.length === 0) {
            setPageCount(0);
            setCgc(cpfOuCnpjTemp);
            setNome(data.nome);
            setCodigoCliente(data.codigoCliente);
        } else {
            toast.error("CPF OU CNPJ Inválido!");
        }
    };

    const handleChangePageSize = (e: any) => {
        setPageCount(0);
        setPageSize(e.target.value);
    }

    return (
        <div>
            <Dialog
                open={show}
                TransitionComponent={Transition}
                fullWidth={true}
                maxWidth={"xl"}
                fullScreen={fullScreen}
                keepMounted
                onClose={() => setShow(false)}
                aria-describedby="alert-dialog-slide-description"
            >
                <IconButton
                    aria-label="close"
                    onClick={() => setShow(false)}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon/>
                </IconButton>
                <DialogTitle>{"Consumidores"}</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        noValidate
                        onSubmit={handleSubmit(onSubmit)}
                        sx={{mt: 2,}}
                    >
                        <Grid container spacing={2}>
                            <Grid item md={3} xs={12}>
                                <TextField
                                    {...register('codigoCliente')}
                                    error={!!errors.codigoCliente}
                                    type={"text"}
                                    label="Código do Cliente"
                                    variant="outlined"
                                    sx={{width: "100%"}}
                                    helperText={errors.codigoCliente?.message}
                                />
                            </Grid>
                            <Grid item md={3} xs={12}>
                                <TextField
                                    {...register('cpfOuCnpj')}
                                    error={!!errors.cpfOuCnpj}
                                    type={"text"}
                                    label="CPF/CNPJ"
                                    variant="outlined"
                                    sx={{width: "100%"}}
                                    helperText={errors.cpfOuCnpj?.message}
                                />
                            </Grid>
                            <Grid item md={3} xs={12}>
                                <TextField
                                    {...register('nome')}
                                    error={!!errors.nome}
                                    type={"text"}
                                    label="Nome"
                                    variant="outlined"
                                    sx={{width: "100%"}}
                                    helperText={errors.nome?.message}
                                />
                            </Grid>
                            <Grid item md={3} xs={12}>
                                <SendButton type={"submit"} variant="contained" size="large">
                                    Buscar
                                </SendButton>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{display: "flex", width: "100%", mt: 3}}>
                        {isLoading && <Loader/>}
                        {!isLoading && page &&
                            <Grid container>
                                {!xs &&
                                    <Grid item xs={12} container sx={{
                                        border: 1,
                                        borderColor: grey[300],
                                        backgroundColor: grey[100],
                                        p: 1,
                                        fontWeight: 800
                                    }}>
                                        <Grid item md sx={{textAlign: "center"}}>Ações</Grid>
                                        <Grid item md sx={{textAlign: "center"}}>Código</Grid>
                                        <Grid item md sx={{textAlign: "center"}}>CPF/CNPJ</Grid>
                                        <Grid item md sx={{textAlign: "center"}}>Nome</Grid>
                                    </Grid>
                                }
                                {page.conteudo.map((row, index) => (
                                    <OrcamentoModalConsumidorRow key={index} row={row} handleAction={handleAction} />
                                ))}
                                <Grid container item xs={12} sx={{pt: 2, display: "flex", alignItems: "center"}}>
                                    <Grid item xs={6} sx={{textAlign: "left"}}>
                                        <Pagination
                                            count={Math.ceil(parseInt(page?.quantidade) / pageSize)}
                                            defaultPage={1}
                                            page={pageCount + 1}
                                            onChange={(event, value) => {
                                                setPageCount(value - 1);
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={6}  sx={{textAlign: "right"}}>
                                        <TextField
                                            id="outlined-select-currency"
                                            select
                                            label="Registros por pagina"
                                            onChange={handleChangePageSize}
                                            size="small"
                                            value={pageSize}
                                            sx={{minWidth: 150}}
                                        >
                                            {PAGE_SIZE_SELECT.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </Grid>
                            </Grid>
                        }
                    </Box>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ModalConsumidor;
